import { TechnicalConfigDrawer } from "features/shared/components/technical-config-tab/TechnicalConfigDrawer";
import { formatTechnicalConfigDrawerSubtitle } from "features/shared/lib/technical-config-tab/drawerFormatters";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { RoomTypeGroup } from "../model/types";

import {
  type BulkRoomConfigOperation,
  useBulkRoomConfig,
  useDeleteRoomConfig,
  useGetRoomSchedulePresets,
  useGetRoomTypeCatalog,
  useGetRoomTypeDetail,
  useSaveRoomConfig,
} from "../api/hooks";
import { useRoomConfigForm } from "../hooks/useRoomConfigForm";
import { useRoomSchedules } from "../hooks/useRoomSchedules";
import { ROOM_CONFIG_TABS } from "../lib/roomConfigFieldDefinitions";
import { mapGroupToCreatePayload } from "../lib/roomConfigForm";
import { RoomBulkActionModal } from "./RoomBulkActionModal";
import { RoomScheduleTab } from "./RoomScheduleTab";

// Previous custom tab panels — keep the files, hide them until the nested APIs return.
// import { RoomHeatingTab } from "./RoomHeatingTab";
// import { RoomHotWaterTab } from "./RoomHotWaterTab";
// import { RoomLuminanceTab } from "./RoomLuminanceTab";
// import { RoomVentilationTab } from "./RoomVentilationTab";

type RoomConfigDrawerProps = {
  bulkGroups?: RoomTypeGroup[];
  group: null | RoomTypeGroup;
  onClose: () => void;
  open: boolean;
  projectId: string;
};

export const RoomConfigDrawer = ({ bulkGroups, group, onClose, open, projectId }: RoomConfigDrawerProps) => {
  const { t } = useTranslation();
  const [bulkAction, setBulkAction] = useState<"delete" | "save" | null>(null);
  const [bulkProgress, setBulkProgress] = useState(0);
  const roomFunctionValue = group?.roomFunctionValue;
  const { data: detail, isLoading } = useGetRoomTypeDetail(projectId, roomFunctionValue);
  const { data: catalog = [] } = useGetRoomTypeCatalog(open);
  const { catalog: schedulePresets, isLoading: isSchedulePresetLoading } = useGetRoomSchedulePresets(open);
  const { isLoading: isSaving, mutate: saveConfig } = useSaveRoomConfig();
  const { isLoading: isDeleting, mutate: deleteConfig } = useDeleteRoomConfig();
  const { isLoading: isBulkProcessing, mutateAsync: runBulkAction } = useBulkRoomConfig();

  const {
    fields,
    formState,
    mapPayload,
    tabs: formTabs,
  } = useRoomConfigForm({
    catalog,
    enabled: open,
    initialConfig: detail?.config,
    isLoading,
  });
  const tabs = useMemo(() => formTabs.map((tab) => ({ ...tab, alwaysVisible: true })), [formTabs]);
  const schedulesState = useRoomSchedules({
    catalog: schedulePresets,
    enabled: open,
    initialSchedules: detail?.config?.schedules,
    isLoading,
    resetKey: roomFunctionValue,
    roomNames: [detail?.config?.roomTypeName, group?.roomTypeName, group?.roomFunction],
    usageEndCet: formState.usageEndCet,
    usageSaturday: Boolean(formState.usageSaturday),
    usageStartCet: formState.usageStartCet,
    usageSunday: Boolean(formState.usageSunday),
  });
  const tabPanels = useMemo(
    () => ({
      [ROOM_CONFIG_TABS.schedules]: (
        <RoomScheduleTab isPresetLoading={isSchedulePresetLoading} schedulesState={schedulesState} />
      ),
      // [ROOM_CONFIG_TABS.heating]: (
      //   <RoomHeatingTab
      //     resetKey={roomFunctionValue}
      //     roomHeatingEnabled={Boolean(formState.roomHeating)}
      //     sharedFields={heatingSharedFields}
      //     systemOptions={linkedSystems.heating}
      //   />
      // ),
      // [ROOM_CONFIG_TABS.hotWater]: (
      //   <RoomHotWaterTab resetKey={roomFunctionValue} systemOptions={linkedSystems.hotWater} />
      // ),
      // [ROOM_CONFIG_TABS.luminance]: <RoomLuminanceTab resetKey={roomFunctionValue} />,
      // [ROOM_CONFIG_TABS.ventilation]: (
      //   <RoomVentilationTab resetKey={roomFunctionValue} systemOptions={ventilationSystemOptions} />
      // ),
    }),
    [isSchedulePresetLoading, schedulesState]
  );

  const isConfigured = Boolean(group?.hasConfig || detail?.config);

  const executeBulkAction = async (action: "delete" | "save") => {
    if (!bulkGroups) return;
    const operations: BulkRoomConfigOperation[] =
      action === "delete"
        ? bulkGroups
            .filter((item) => item.hasConfig)
            .map((item) => ({ kind: "delete", roomFunctionValue: item.roomFunctionValue }))
        : bulkGroups.map((item) => ({
            data: item.hasConfig
              ? { ...mapPayload(), schedules: schedulesState.getPayload() }
              : mapGroupToCreatePayload(item),
            ...(!item.hasConfig ? { followUpData: { ...mapPayload(), schedules: schedulesState.getPayload() } } : {}),
            hasConfig: item.hasConfig,
            kind: "save",
            roomFunctionValue: item.roomFunctionValue,
          }));

    if (operations.length === 0) return;

    try {
      await runBulkAction({
        onProgress: (completed, total) => setBulkProgress((completed / total) * 100),
        operations,
        projectId,
        successMessageKey: action === "delete" ? "TechnicalData.room.deleteSuccess" : "TechnicalData.room.saveSuccess",
      });
      setBulkAction(null);
      onClose();
    } catch {
      if (action === "save") setBulkAction(null);
    }
  };

  const handleSave = () => {
    if (!roomFunctionValue || !group) return;
    if (bulkGroups) {
      setBulkProgress(0);
      setBulkAction("save");
      void executeBulkAction("save");
      return;
    }
    saveConfig(
      isConfigured
        ? {
            data: { ...mapPayload(), schedules: schedulesState.getPayload() },
            hasConfig: true,
            projectId,
            roomFunctionValue,
          }
        : { data: mapGroupToCreatePayload(group), hasConfig: false, projectId, roomFunctionValue },
      { onSuccess: onClose }
    );
  };

  const handleDelete = () => {
    if (!roomFunctionValue || !isConfigured) return;
    if (bulkGroups) {
      setBulkProgress(0);
      setBulkAction("delete");
      return;
    }
    deleteConfig({ projectId, roomFunctionValue }, { onSuccess: onClose });
  };

  return (
    <>
      <TechnicalConfigDrawer
        deleteLabel={String(t("TechnicalData.room.removeConfig"))}
        fields={fields}
        isConfigured={isConfigured}
        isDeleting={bulkGroups ? isBulkProcessing : isDeleting}
        isLoading={isLoading}
        isSubmitting={bulkGroups ? isBulkProcessing : isSaving}
        onClose={onClose}
        onDelete={handleDelete}
        onSave={handleSave}
        open={open}
        subtitle={
          bulkGroups
            ? String(
                t("TechnicalData.room.roomFunctionsCount", "{{count}} room functions", {
                  count: bulkGroups.length,
                })
              )
            : group
            ? formatTechnicalConfigDrawerSubtitle(group.roomCount, t("TechnicalData.room.rooms"), group.totalArea)
            : undefined
        }
        tabOrientation="vertical"
        tabPanels={tabPanels}
        tabs={tabs}
        title={
          bulkGroups
            ? group?.roomTypeName ?? t("TechnicalData.room.unassigned", "Unassigned")
            : group?.roomFunction ?? t("TechnicalData.room.drawerTitle")
        }
        widthClassName="w-[88rem]"
      />
      <RoomBulkActionModal
        action={bulkAction === "delete" ? "deleteConfig" : "saveConfig"}
        count={
          bulkAction === "delete" ? bulkGroups?.filter((item) => item.hasConfig).length ?? 0 : bulkGroups?.length ?? 0
        }
        isProcessing={isBulkProcessing || bulkAction === "save"}
        onCancel={() => setBulkAction(null)}
        onConfirm={() => void executeBulkAction("delete")}
        open={Boolean(bulkAction)}
        progress={bulkProgress}
      />
    </>
  );
};
